// src/components/admin/AdminHome.js
import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Typography,
    useTheme,
    useMediaQuery,
    Avatar,
    Grid2 as Grid,
    Link,
    Stack,
    Button,
    Snackbar,
    Alert,
} from '@mui/material';
import RequestService from '../../services/RequestService';
import TableComponent from '../../widgets/TableComponent';
import PeopleIcon from '@mui/icons-material/People';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RecentActorsOutlinedIcon from '@mui/icons-material/RecentActorsOutlined';

import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import NewCompanyDialog from '../../widgets/admin/NewCompanyDialog';
import EditCompanyDialog from '../../widgets/admin/EditCompanyDialog';
import CompanyList from '../../widgets/admin/CompanyList';
import AdminAccessDialog from '../../widgets/admin/AdminAccessDialog';
import ConfirmDialog from '../../widgets/admin/ConfirmDialog';
import CompanyCardsDialog from '../../widgets/admin/CompanyCardsDialog';
import { toCents } from '../../utils/toCents';

const AdminHome = () => {
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isCardsDialogOpen, setIsCardsDialogOpen] = useState(false);
    const [companyToToggle, setCompanyToToggle] = useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const response = await RequestService.getCompanies();
                setCompanies(response);
            } catch (err) {
                setError('Erro ao carregar empresas.');
                setCompanies([]);
            } finally {
                setLoading(false);
            }
        };

        fetchCompanies();
    }, []);

    const handleOpenAddDialog = () => setIsAddDialogOpen(true);
    const handleCloseAddDialog = () => setIsAddDialogOpen(false);

    const handleSuccessAdd = async () => {
        try {
            const data = await RequestService.getCompanies();
            setCompanies(Array.isArray(data) ? data : []);
            setSnackbarMessage('Empresa criada com sucesso!');
            setSnackbarOpen(true);
        } catch (err) {
            setError('Erro ao recarregar empresas.');
            setCompanies([]);
        }
    };

    const handleConfirmToggleCompany = async () => {
        if (!companyToToggle) return;
        try {
            await RequestService.toggleCompanyStatus(
                companyToToggle.id,
                !companyToToggle.active
            );
            const updated = await RequestService.getCompanies();
            setCompanies(updated);
            setSnackbarMessage(
                `Empresa ${companyToToggle.active ? 'desativada' : 'ativada'} com sucesso!`
            );
            setSnackbarOpen(true);
        } catch (error) {
            setSnackbarMessage('Erro ao alterar status da empresa.');
            setSnackbarOpen(true);
        } finally {
            setCompanyToToggle(null);
            setIsConfirmDialogOpen(false);
        }
    };

    const handleOpenEditDialog = (company) => {
        setSelectedCompany(company);
        setIsEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setSelectedCompany(null);
        setIsEditDialogOpen(false);
    };

    const handleSuccessEdit = async () => {
        const updated = await RequestService.getCompanies();
        setCompanies(updated);
        setSnackbarMessage('Empresa atualizada com sucesso!');
        setSnackbarOpen(true);
    };

    const columns = [
        {
            label: 'Logo',
            field: 'photoUrl',
            renderCell: (url) => (
                <Avatar
                    src={url}
                    alt="Company Logo"
                    sx={{ width: 40, height: 40 }}
                />
            ),
        },
        { label: 'Nome', field: 'name' },
        { label: 'Usuários', field: 'userCount', align: 'center' },
        {
            label: 'Plano',
            field: 'currentPlan',
            align: 'center',
            renderCell: (value, row) => (
                <Typography variant="body2" sx={{ color: '#292929' }}>
                    {row.currentPlan?.name ?? '-'}
                </Typography>
            ),
        },
        {
            label: '',
            field: 'actions',
            align: 'right',
            renderCell: (value, row, stopPropagation) => (
                <Stack direction="row" spacing={2}>
                    <Link
                        component="button"
                        onClick={(event) => {
                            stopPropagation(event);
                            setSelectedCompany(row);
                            setIsAccessDialogOpen(true);
                        }}
                        underline="none"
                        className="action-link"
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <PeopleIcon fontSize="small" color="#1F83FF" />
                            <Typography>Acessos</Typography>
                        </Stack>
                    </Link>
                    <Link
                        component="button"
                        onClick={(event) => {
                            stopPropagation(event);
                            setSelectedCompany(row);
                            setIsCardsDialogOpen(true);
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <RecentActorsOutlinedIcon
                                fontSize="small"
                                color="#1F83FF"
                            />
                            <Typography>Cartões</Typography>
                        </Stack>
                    </Link>
                    <Link
                        component="button"
                        onClick={(event) => {
                            stopPropagation(event);
                            handleOpenEditDialog(row);
                        }}
                        underline="none"
                        className="action-link"
                        sx={{ color: 'red' }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            color="#1F83FF"
                        >
                            <EditIcon sx={{ color: '#1F83FF' }} />
                            <Typography>Editar</Typography>
                        </Stack>
                    </Link>
                    <Link
                        component="button"
                        onClick={(event) => {
                            stopPropagation(event);
                            setCompanyToToggle(row);
                            setIsConfirmDialogOpen(true);
                        }}
                        underline="none"
                        className="action-link"
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <PowerSettingsNewIcon
                                fontSize="small"
                                sx={{
                                    color: row.active ? '#FF4D4F' : '#1F83FF',
                                }}
                            />
                            <Typography
                                sx={{
                                    color: row.active ? '#FF4D4F' : '#1F83FF',
                                }}
                            >
                                {row.active ? 'Desativar' : 'Ativar'}
                            </Typography>
                        </Stack>
                    </Link>
                </Stack>
            ),
        },
    ];

    const renderMobileList = () => (
        <CompanyList
            companies={companies}
            onAccessClick={(company) => setSelectedCompany(company)}
            onEditClick={handleOpenEditDialog}
            onDeactivateClick={(company) => {
                console.log('Desativar:', company);
                // handleDeactivate(company) se quiser implementar
            }}
        />
    );

    if (loading) return <div>Carregando...</div>;
    if (error) return <div>{error}</div>;

    return (
        <Box sx={{ padding: 2, backgroundColor: '#F5F5F5' }}>
            {/* <Typography
                variant="h4"
                sx={{ textAlign: 'center', mb: 4, fontWeight: 500 }}
            >
                Bem-vindo, SuperAdmin!
            </Typography> */}

            <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                wrap="wrap"
            >
                <Grid container item xs={12} md={9} spacing={2}>
                    {/* No Filters yet */}
                    {/* <Grid item xs={12} sm={6} md={3}>
                                    <FormControl sx={{ minWidth: 200, width: '100%' }}>
                                        <InputLabel>Jogo</InputLabel>
                                        <Select
                                            value={gameId}
                                            onChange={(e) => setGameId(e.target.value)}
                                        >
                                            <MenuItem value="">Todos</MenuItem>
                                            {games.map((game) => (
                                                <MenuItem key={game.id} value={game.id}>
                                                    {game.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid> */}

                    {/* <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        variant="contained"
                                        sx={{
                                            minHeight: '47px',
                                        }}
                                        onClick={fetchPlayers}
                                        fullWidth
                                    >
                                        Aplicar Filtros
                                    </Button>
                                </Grid> */}
                </Grid>

                {/* Right side: “Nova Missão” button */}
                <Grid item xs={12} md="auto">
                    <Button
                        variant="contained"
                        sx={{
                            minHeight: '47px',
                        }}
                        endIcon={<AddCircleOutlineOutlinedIcon />}
                        onClick={handleOpenAddDialog}
                    >
                        Nova Empresa
                    </Button>
                </Grid>
            </Grid>

            {isMobile ? (
                renderMobileList()
            ) : (
                <TableComponent columns={columns} rows={companies} />
            )}

            <NewCompanyDialog
                open={isAddDialogOpen}
                onClose={handleCloseAddDialog}
                onSuccess={handleSuccessAdd}
            />

            <EditCompanyDialog
                open={isEditDialogOpen}
                onClose={handleCloseEditDialog}
                onSuccess={handleSuccessEdit}
                company={selectedCompany}
            />

            <AdminAccessDialog
                open={isAccessDialogOpen}
                onClose={() => setIsAccessDialogOpen(false)}
                company={selectedCompany}
            />

            <ConfirmDialog
                open={isConfirmDialogOpen}
                title="Por favor, confirme a ação"
                message={`Tem certeza que deseja ${companyToToggle?.active ? 'desativar' : 'ativar'} a empresa "${companyToToggle?.name}"?`}
                onClose={() => setIsConfirmDialogOpen(false)}
                onConfirm={handleConfirmToggleCompany}
            />

            <CompanyCardsDialog
                open={isCardsDialogOpen}
                onClose={() => setIsCardsDialogOpen(false)}
                company={selectedCompany}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="success"
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminHome;
